import React, { useEffect, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useRecoilState } from "recoil";
import { useSearchParams, useNavigate } from "react-router-dom";

import { useTheme } from "@/b2b/context/ThemeContext";
import {
  partnerJourneys,
  withoutLeadPartnerJourney,
  navigationRules,
} from "@/b2b/config/partnerJourney";
import { motion } from "framer-motion";
import { headingVariants } from "@/lib/data";
import Header from "@/b2b/components/Header";
import Div100vh from "react-div-100vh";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import { CONSENT_MESSAGES, ALT_PHONE_COPY } from "@/lib/data";
import DynamicInputForm from "@/b2b/components/DynamicInputForm";
import BureauNumbersCard from "@/b2b/components/BureauNumbersCard";
import { onboardingFormDataAtom } from "@/recoil/atom/onboardingFormDataAtom";
import { handleAndTrackError, getDataB2B } from "@/lib/helper";
import {
  storeBureauPhoneHints,
  readBureauPhoneHints,
  readDisplayHints,
  formatHintsForDisplay,
  matchesAnyHint,
} from "@/lib/bureauPhoneHints";

const PartnerDynamicScreen = ({ step }) => {
  const navigate = useNavigate();
  const partner = localStorage.getItem("partnerIdentifier");

  const theme = useTheme();

  // const [searchParams] = useSearchParams();
  // const partner = searchParams.get("partner") || "default";
  // const step = searchParams.get("step") || "eligibility-check";

  const [formData, setFormData] = useRecoilState(onboardingFormDataAtom);
  const [disableNextBtn, setDisableNextBtn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");

  const [error, setError] = useState("");

  // DB-driven partners have no hardcoded journey → fall back to the canonical
  // CirclePe journey so the dynamic steps work instead of crashing.
  const journey =
    withoutLeadPartnerJourney[partner] || partnerJourneys.circlepe || [];
  const screenConfig = journey?.find((screen) => screen.step === step);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Every phone number the bureau reported for this journey — scoped by journey token so two
  // leads open in one browser can't validate against each other's numbers.
  //
  // Read on every render, not memoised: localStorage is mutated outside React after each
  // submit, so a `[step]` memo went stale on a second attempt from the same step. See the
  // fuller note in B2BDynamicScreen.
  const bureauPhoneHints = readBureauPhoneHints(
    localStorage.getItem("user_identifier"),
  );
  const bureauPhoneHintText = formatHintsForDisplay(bureauPhoneHints);

  // What we SHOW. Falls back to the pre-upgrade global `extractedNumber` when this journey has
  // no stored list, so customers who were already on this screen when the new build shipped
  // still see their number instead of a blank prompt. Deliberately NOT used for validation —
  // that stays on `bureauPhoneHints` above, because the legacy key is global and could belong
  // to a different lead in the same browser. See readDisplayHints for the full reasoning.
  const displayHints = readDisplayHints(localStorage.getItem("user_identifier"));
  const isAltPhoneStep = step === "alt-phone";

  // Mismatch screen uses ALT_PHONE_COPY, the single source of truth for this wording — same as
  // B2BDynamicScreen, so /b/ and /p/ customers read exactly the same explanation.
  const headingText = isAltPhoneStep
    ? ALT_PHONE_COPY.heading
    : screenConfig?.heading;

  // One short sentence — the numbers live in BureauNumbersCard so they can be scanned.
  const subHeadingText = isAltPhoneStep
    ? ALT_PHONE_COPY.body
    : screenConfig?.subHeading;

  // "Alt Phone" is internal shorthand; without this the input reads "Enter Your Alt Phone".
  // Overridden here rather than in the 27 partner configs that carry the label.
  const altPhoneAwareFields = isAltPhoneStep
    ? screenConfig?.fields?.map((field) =>
        field.name === "alt_phone"
          ? {
              ...field,
              placeholder: ALT_PHONE_COPY.inputPlaceholder(
                displayHints.length > 0,
              ),
            }
          : field,
      )
    : screenConfig?.fields;

  const startOnboardingJourney = async () => {
    try {
      const token = localStorage.getItem("user_identifier");
      const result = await getDataB2B("b2b/onboard/start", token);
      setJourneyToken(result?.token);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const handleNext = async () => {
    // Check against the bureau's candidates before spending a billable pull. Accepts a match
    // on ANY of them, and passes through when the bureau named none — see B2BDynamicScreen.
    if (step === "alt-phone" && !matchesAnyHint(formData?.alt_phone, bureauPhoneHints)) {
      setError(
        ALT_PHONE_COPY.mismatchError(bureauPhoneHintText),
      );
      return;
    }

    if (screenConfig.validateText?.(formData)) {
      setError("Please enter correct details.");
    } else if (screenConfig.validate(formData) && !disableNextBtn) {
      try {
        setLoading((prevState) => !prevState);
        const token = localStorage.getItem("user_identifier");
        const requiredFormData = {};

        if (step === "alt-phone") {
          requiredFormData["pan_number"] = localStorage.getItem("pan_number");
        }
        // if (screenId === "alt-phone") {
        //   requiredFormData["pan_number"] = pdata["pan_number"];
        //   requiredFormData["pincode"] = pdata["pincode"];
        // }
        screenConfig.fields.forEach((field) => {
          if (formData.hasOwnProperty(field.name)) {
            requiredFormData[field.name] = formData[field.name];
          }
        });

        let response = await screenConfig.onSubmit(
          requiredFormData,
          screenConfig?.apiPath,
          screenConfig?.click_name,
          screenConfig?.click_source,
          token,
        );
        const platform =
          navigator?.userAgentData?.platform ||
          navigator.platform ||
          localStorage.getItem("UTM_SOURCE");

        console.log(screenConfig.apiPath, response);
        switch (screenConfig.apiPath) {
          case "approval/check":
            if (response.success) {
              if (response?.data?.isEligible) {
                navigate(
                  `/p/${localStorage.getItem("user_identifier")}?step=${
                    navigationRules[partner]?.panChecked ||
                    navigationRules.default.isEligibleCongrats
                  }`,
                );
              } else {
                navigate(
                  `/p/${localStorage.getItem("user_identifier")}?step=${
                    navigationRules[partner]?.notHardEligible ||
                    navigationRules.default.notHardEligible
                  }`,
                );
              }
            } else {
              if (response?.data?.status === "PHONE_MISMATCH") {
                // Keep every candidate the bureau named — see B2BDynamicScreen for why.
                storeBureauPhoneHints(token, response?.data?.extractedNumbers);
                navigate(
                  `/p/${localStorage.getItem("user_identifier")}?step=${
                    navigationRules[partner]?.phoneMismatch ||
                    navigationRules.default.phoneMismatch
                  }`,
                );
              } else if (
                [
                  "NEED_PAN",
                  "VALIDATION_ERROR",
                  "BUREAU_PULL_FAILED",
                  "INPUT_ERROR",
                  "CONFLICT",
                  "ERROR",
                ].includes(response?.data?.status)
              ) {
                // Vendor/input error or missing PAN — NOT a credit rejection.
                // Throw back to the PAN screen so the check re-runs.
                navigate(
                  `/p/${localStorage.getItem("user_identifier")}?step=${
                    navigationRules[partner]?.panNotChecked ||
                    navigationRules.default.panNotChecked
                  }`,
                );
              }
            }
            break;

          // The alt-phone step of the `withoutLeadPartnerJourney` posts to
          // `b2b/onboard/check-eligibility`, but this switch only handled `approval/check`.
          // With no matching case the response was discarded and no navigation happened —
          // on /p/ links the "Confirm" button on the phone-mismatch screen did nothing at
          // all. Mirrors the equivalent branch in B2BDynamicScreen.
          case "b2b/onboard/check-eligibility":
            if (response?.status === "PHONE_MISMATCH") {
              storeBureauPhoneHints(token, response?.extractedNumbers);
              navigate(
                `/p/${localStorage.getItem("user_identifier")}?step=${
                  navigationRules[partner]?.phoneMismatch ||
                  navigationRules.default.phoneMismatch
                }`,
              );
            } else if (
              [
                "NEED_PAN",
                "VALIDATION_ERROR",
                "BUREAU_PULL_FAILED",
                "INPUT_ERROR",
                "CONFLICT",
                "ERROR",
              ].includes(response?.status)
            ) {
              navigate(
                `/p/${localStorage.getItem("user_identifier")}?step=${
                  navigationRules[partner]?.panNotChecked ||
                  navigationRules.default.panNotChecked
                }`,
              );
            } else if (response?.isEligible) {
              navigate(
                `/p/${localStorage.getItem("user_identifier")}?step=${
                  navigationRules[partner]?.panChecked ||
                  navigationRules.default.isEligibleCongrats
                }`,
              );
            } else {
              navigate(
                `/p/${localStorage.getItem("user_identifier")}?step=${
                  navigationRules[partner]?.notHardEligible ||
                  navigationRules.default.notHardEligible
                }`,
              );
            }
            break;
        }
      } catch (err) {
        console.log(err);

        handleAndTrackError(error, {
          apiPath: screenConfig.apiPath,
        });
      } finally {
        setLoading((prevState) => !prevState);
      }
    } else if (disableNextBtn) setError("Please try again after some time!");
    else {
      setError("Please enter all required details!");
    }
  };

  useEffect(() => {
    if (loading) {
      const loadingMessages = [
        "Loading...",
        "Almost there...",
        "Finishing up...",
        "Almost done...",
      ];
      let index = 0;

      // Change loading text at intervals
      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingText(loadingMessages[index]);
      }, 3000); // Change text every 1 second

      // Clear interval on unmount or when loading stops
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      setError("");
    }
    setDisableNextBtn(false);
  }, [formData]);

  // No config for this step — render nothing instead of crashing below.
  if (!screenConfig) return null;

  if (theme) {
    return (
      <Div100vh
        className={`flex flex-col justify-start p-8 sm:p-8 gap-6 ${theme?.backgroundColor} ${theme?.text}`}
      >
        <div className="flex flex-col gap-8">
          <Header />
          <div className="flex flex-col gap-1">
            <motion.h1
              className="reveal-heading"
              initial="hidden"
              animate="visible"
              variants={headingVariants}
            >
              <span
                className={`heading mb-heading-subheading ${theme?.headingColor}`}
              >
                {headingText}
              </span>
            </motion.h1>
            {subHeadingText && (
              <motion.h1
                className="reveal-heading"
                initial="hidden"
                animate="visible"
                variants={headingVariants}
              >
                <span
                  className={`${screenConfig.subheadingSize || "text-xs"} ${
                    theme?.subHeadingColor
                  }`}
                >
                  {subHeadingText}
                </span>
              </motion.h1>
            )}
          </div>

          {/* The numbers the customer has to act on — their own card so they are the visual
              anchor rather than five digits buried in body text. Same as B2BDynamicScreen. */}
          {isAltPhoneStep && (
            <BureauNumbersCard hints={displayHints} theme={theme} />
          )}
          <DynamicInputForm
            formConfig={altPhoneAwareFields}
            formData={formData}
            setFormData={setFormData}
            setError={setError}
            setDisableNextBtn={setDisableNextBtn}
            setButtonLoading={setLoading}
          />
        </div>
        <div className="flex flex-col h-full items-end justify-end gap-1">
          {step === "eligibility-check" && (
            <span className={`text-3xs ${theme?.textColor} text-center m-2`}>
              {CONSENT_MESSAGES.PAN_AND_BUREAU}
            </span>
          )}

          {/* Same slot the eligibility screen uses for its consent line — fine print belongs
              next to the action, not crowding the explanation at the top. */}
          {isAltPhoneStep && (
            <span className={`text-3xs ${theme?.textColor} text-center m-2`}>
              {ALT_PHONE_COPY.reassurance}
            </span>
          )}

          <PrimaryButton
            text={loading ? loadingText : screenConfig?.click_name || "Next"}
            loading={loading}
            handleClick={handleNext}
            error={error}
            disabled={loading || error !== "" || disableNextBtn}
            // className={theme.primary}
          />

          {/* <div
            className={`w-full text-center text-[0.678rem] ${
              theme?.text || "text-[#aeaeae]"
            }`}
          >
            powered by{" "}
            <a
              target="_blank"
              href="https://fexprime.com"
              className="hover:underline hover:text-primary"
            >
              Fexprime
            </a>
          </div> */}
        </div>
      </Div100vh>
    );
  }
  return null;
};

export default PartnerDynamicScreen;
